import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { Laterale } from '../components/Laterale.jsx';
import { Paysage } from '../components/Paysage.jsx';
import { Indicateurs } from '../components/Indicateurs.jsx';
import { listerVoyages, libelleStatut } from '../lib/trips.js';
import { formaterMontant, resumerPeriodeCourte } from '../lib/format.js';

/**
 * Teinte du bandeau, tirée de l'identifiant du voyage.
 *
 * Déduite plutôt que tirée au sort : la couleur doit rester la même d'une
 * visite à l'autre, sans quoi elle n'aide pas à retrouver un voyage dans la
 * grille — elle ne ferait que scintiller. L'identifiant plutôt que le nom,
 * pour deux raisons : il est aléatoire, donc les teintes se répartissent au
 * lieu de s'agglutiner sur des noms de longueur voisine ; et il ne bouge pas
 * quand on renomme le voyage.
 */
function teinte(id) {
  let empreinte = 5381;
  for (let i = 0; i < id.length; i += 1) {
    empreinte = ((empreinte << 5) + empreinte + id.charCodeAt(i)) >>> 0;
  }
  return empreinte % 4;
}

/** « 3 voyages, dont 1 archivé » — l'état du compte en une ligne. */
function resumerCollection(voyages) {
  const total = voyages.length;
  if (total === 0) return 'Aucun voyage';

  const pluriel = total > 1 ? 's' : '';
  const archives = voyages.filter((v) => v.status === 'archived').length;
  const enCours = voyages.filter((v) => v.status === 'planning').length;

  const details = [
    enCours > 0 ? `${enCours} en préparation` : null,
    archives > 0 ? `${archives} archivé${archives > 1 ? 's' : ''}` : null,
  ].filter(Boolean);

  return details.length > 0
    ? `${total} voyage${pluriel}, dont ${details.join(' et ')}`
    : `${total} voyage${pluriel}`;
}

export function Dashboard() {
  const { user } = useAuth();

  const [voyages, setVoyages] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;

    listerVoyages()
      .then((liste) => !annule && setVoyages(liste))
      .catch((echec) => !annule && setErreur(echec.message));

    return () => {
      annule = true;
    };
  }, []);

  return (
    <div className="cadre">
      <Laterale actif="voyages" />

      <main>
        <header className="entete">
          <div>
            <h1>Bonjour {user.firstName}</h1>
            <p className="entete__sous-titre">
              {voyages === null ? 'Chargement…' : resumerCollection(voyages)}
            </p>
          </div>
        </header>

        {erreur && (
          <p className="erreur" role="alert">
            {erreur}
          </p>
        )}

        {voyages?.length > 0 && <Indicateurs voyages={voyages} />}

        {voyages?.length === 0 && (
          <section className="verre vide">
            <h2>Aucun voyage pour le moment</h2>
            <p>Décrivez votre projet, l’itinéraire viendra ensuite.</p>
            <Link className="bouton bouton--lien" to="/voyages/nouveau">
              Créer mon premier voyage
            </Link>
          </section>
        )}

        {voyages?.length > 0 && (
          <ul className="cartes">
            {voyages.map((voyage) => {
              const budget = formaterMontant(voyage.budget.amount, voyage.budget.currency);
              const meta = [
                voyage.destination.label,
                resumerPeriodeCourte(voyage),
                voyage.participants.length > 0
                  ? `${voyage.participants.length} pers.`
                  : null,
                budget,
              ]
                .filter(Boolean)
                .join(' · ');

              return (
                <li key={voyage.id}>
                  <Link
                    className={`verre carte${voyage.status === 'archived' ? ' carte--archivee' : ''}`}
                    to={`/voyages/${voyage.id}`}
                  >
                    <div className="carte__bandeau">
                      <Paysage variante={teinte(voyage.id)} />
                      <span className="badge">{libelleStatut(voyage.status)}</span>
                    </div>
                    <div className="carte__corps">
                      <h2 className="carte__titre">
                        {/* Verte dès qu'un itinéraire existe : c'est ce qui
                            distingue un voyage prêt d'une simple intention. */}
                        <span
                          className={`pastille${voyage.hasItinerary ? ' pastille--prete' : ''}`}
                          title={
                            voyage.hasItinerary
                              ? `Itinéraire établi — ${voyage.itineraryDays} jour${voyage.itineraryDays > 1 ? 's' : ''}`
                              : 'Aucun itinéraire pour l’instant'
                          }
                        >
                          <span className="invisible">
                            {voyage.hasItinerary ? 'Itinéraire établi' : 'Aucun itinéraire'}
                          </span>
                        </span>
                        {voyage.name}
                      </h2>
                      <p className="carte__ligne">{meta}</p>
                      {voyage.tripTypes.length > 0 && (
                        <p className="carte__types">
                          {voyage.tripTypes.slice(0, 3).map((type) => (
                            <span key={type} className="carte__type">
                              {type}
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}

            <li>
              <Link className="carte--vide" to="/voyages/nouveau">
                <span aria-hidden="true">+</span>
                <span>Planifier un nouveau voyage</span>
              </Link>
            </li>
          </ul>
        )}
      </main>
    </div>
  );
}
